import he from 'he'
import sortBy from 'lodash/sortBy'
import { CATALOG_FILTER, CATALOG_SORT, NOT_FILTERS } from 'lib/core/resources/constants'
import { getChildrenCategories } from 'lib/core/resources/helpers'
import { AttributeSettingsMap } from './attributeSettings'
import {
  CategoryList,
  MagentoLayerFilter,
  MagentoProducts,
  MagentoSearchResultPageInfo,
  MagentoSortFields,
  PageNormalized
} from 'lib/types'

import {
  Filters,
  FiltersMap,
  FilterItem,
  FilterItemData,
  FilterOptionType,
  FilterOptions,
  FilterApplied,
  FilterItemRenderer
} from './types'

export const normalizePageInfo = (pageInfo: MagentoSearchResultPageInfo, total: number | undefined): PageNormalized => {
  return {
    pageSize: pageInfo.page_size,
    currentPage: pageInfo.current_page,
    totalPages: total ? Math.ceil(total / pageInfo.page_size) : 0,
    totalItems: total ?? 0
  }
}

export const getFiltersCollection = (payload?: MagentoProducts) => {
  const filters: MagentoLayerFilter[] = []
  payload?.filters?.forEach((filter) => {
    const {
      request_var: requestVar,
      filter_items: filterItemsOptions
    } = filter

    const filterItems = filterItemsOptions.map(item => {
      return {
        ...item,
        label: he.decode(item.label)
      }
    })

    filters.push({
      ...filter,
      request_var: requestVar,
      filter_items: filterItems
    })
  })
  return filters
}

export const getFiltersMap = (payload?: MagentoProducts): FiltersMap => {
  return payload?.filters.reduce((obj: Record<string, FilterItem>, filter) => {
    const {
      name,
      request_var: requestVar,
      filter_items: options
    } = filter

    obj[requestVar] = {
      name,
      items: options.reduce((itemsMap: Record<string, FilterItemData>, option) => {
        const { label, value_string: value } = option
        itemsMap[value] = {
          value,
          label: he.decode(label)
        }
        return itemsMap
      }, {})
    }

    return obj
  }, {}) ?? {}
}

export const objToPath = (obj: undefined | Record<string, string[]> = {}): string => { // TODO: tests
  const keys = Object.keys(obj)

  if (!keys.length) {
    return ''
  }

  return Object.keys(obj).reduce((path, key) => {
    const str = path + `${key}/${obj[key]?.join(',')}/`
    return str
  }, '/')
}

export const pathToObj = (string: undefined | string = ''): Record<string, string[]> => { // TODO: tests
  if (!string) {
    return {}
  }

  return string
    .substr(1)
    .slice(0, -1)
    .split('/')
    .reduce((obj: Record<string, string[]>, el, index, arr) => {
      if (index % 2 === 0) {
        const [key, value] = arr.slice(index, index + 2)
        obj[key] = value?.split(',')
      }
      return obj
    }, {})
}

export const getFiltersQuery = (toolbarFilters: Filters) => {
  const filtersQuery: Filters = {}
  for (const requestVar in toolbarFilters) {
    const filterValue = toolbarFilters[requestVar]
    if (filterValue) {
      filtersQuery[requestVar] = filterValue
    }
  }

  return filtersQuery
}

export const getFiltersParams = (filtersAreAnd: boolean, args: { filters: Filters, categoryId?: number }) => {
  const { filters, categoryId } = args
  const categoryFilter = filters[CATALOG_FILTER.CATEGORY] ?? []
  const categoryFilterValue: string[] = []

  let params: Record<string, any> = {}

  if (categoryFilter.length) {
    categoryFilterValue.push(...categoryFilter)
  }

  if (categoryId && !categoryFilter.length) {
    categoryFilterValue.push(categoryId.toString())
  }

  if (categoryFilterValue.length) {
    params.category_id = {
      eq: categoryFilterValue[0]
    }
  }

  const processedFilters = Object.keys(filters).reduce((obj: Record<string, any>, filter) => {
    const element = filters[filter]
    if (!element || NOT_FILTERS.includes(filter)) {
      return obj
    }

    const isPrice = filter === CATALOG_FILTER.PRICE
    if (isPrice) {
      const [price] = element
      const [from, to] = price.split('_') as [string | undefined, string | undefined]
      return {
        ...obj,
        [filter]: {
          from: from?.match(/\d/g)?.join('') ?? '',
          to: to?.match(/\d/g)?.join('') ?? ''
        }
      }
    }

    return {
      ...obj,
      [filter]: filtersAreAnd ? { in: element } : { eq: element[0] }
    }
  }, {})

  if (Object.keys(processedFilters).length) {
    params = {
      ...params,
      ...processedFilters
    }
  }

  return params
}

export const getCurrentSort = (sort?: MagentoSortFields): string[] => {
  const obj = pathToObj(window.location.hash.substring(1))
  const currentSort = obj?.[CATALOG_SORT.SORT_BY]
  return currentSort ?? (sort?.default ? [sort?.default, CATALOG_SORT.ASC] : undefined)
}

export interface GetFilterOptionsDeps {
  attrSettings?: AttributeSettingsMap
  categoryId?: number
  categoryList: CategoryList
  currentFilters: Filters
  filtersMap: FiltersMap
  filtersCollection: MagentoLayerFilter[]
  filterItemRenderer?: FilterItemRenderer
}

export const getFilterOptions = ({
  attrSettings,
  categoryId,
  categoryList,
  currentFilters,
  filtersCollection,
  filtersMap,
  filterItemRenderer = ({ count, label }) => (`${label} ${count ? `(${count})` : ''}`)
}: GetFilterOptionsDeps): FilterOptions[] => {
  const readCategoriesFromFilter = true
  const elements: FilterOptions[] = []
  const plpCatId = categoryId?.toString()

  filtersCollection.forEach((filter, i) => {
    const { request_var: requestVar, filter_items: options } = filter

    if (!options) {
      return null
    }

    const items: FilterOptionType[] = []

    if (!readCategoriesFromFilter && plpCatId && requestVar === CATALOG_FILTER.CATEGORY) {
      const category = categoryList.id[plpCatId]
      getChildrenCategories(category, categoryList)?.forEach(cat => {
        if (cat.isActive) {
          items.push({
            label: filterItemRenderer({
              label: cat.name,
              count: undefined
            }),
            requestVar,
            value: cat.id.toString()
          })
        }
      })
    }

    options.forEach(option => {
      const {
        label,
        items_count: count,
        value_string: value
      } = option

      if (requestVar === CATALOG_FILTER.CATEGORY) {
        if (!readCategoriesFromFilter) {
          return
        }

        if (value === plpCatId) {
          return
        }

        const category = categoryList.id[value]
        if (!category || !category?.isActive || category.parentCategory?.id.toString() !== plpCatId) {
          return
        }
      }

      items.push({
        label: filterItemRenderer({ label, count }),
        requestVar,
        value
      })
    })

    const currentFilterValue = currentFilters[requestVar]
    const selected: FilterOptionType[] | null = currentFilterValue ? (
      requestVar === CATALOG_FILTER.PRICE ? currentFilterValue.map(el => ({
        label: priceValueToLabel(el),
        value: el,
        requestVar
      })) : currentFilterValue.map(el =>
        ({
          ...filtersMap[requestVar].items[el],
          value: el,
          requestVar
        }))
    ) : null

    const name = filtersMap[requestVar].name

    elements.push({
      selected,
      name,
      items,
      settings: attrSettings?.[requestVar],
      code: requestVar
    })
  })

  return sortBy(elements, ['settings.position', 'name'])
}

export interface GetCurrentFiltersDeps {
  currentFilters: Filters
  filtersMap: FiltersMap
}

export const getCurrentFilters = ({
  currentFilters,
  filtersMap
}: GetCurrentFiltersDeps): FilterApplied[] => {
  const appliedFilters: FilterApplied[] = []
  const currentFiltersKeys = Object.keys(currentFilters).reduce((acc: string[], filter) => {
    if (!NOT_FILTERS.includes(filter)) {
      acc.push(filter)
    }
    return acc
  }, [])

  currentFiltersKeys.forEach(requestVar => {
    const filter = filtersMap[requestVar]
    if (!filter) {
      return
    }

    const valueId = currentFilters[requestVar]
    const attrLabel = filtersMap[requestVar].name
    const filterLabel = valueId.map(_id => filtersMap[requestVar].items[_id]?.label)
    const attrValue = requestVar === CATALOG_FILTER.PRICE ? valueId.map(_id => priceValueToLabel(_id)) : filterLabel

    appliedFilters.push({
      requestVar,
      label: attrLabel,
      valueLabel: attrValue,
      valueId: valueId
    })
  })

  return appliedFilters
}

const priceValueToLabel = (value: string): string => {
  return value.replace('_', '-')
}
